import {
  TextField,
  Typography,
  Box,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { useState, useMemo, useEffect } from 'react';

import { ContentCopy, Check } from '@mui/icons-material';

import { Or2Button } from '@/components/button/Or2Button';
import { Or2Dialog } from '@/components/feedback/Or2Dialog';

import { env } from '@/env';

interface Props {
  open: boolean;
  onClose: () => void;
}

export const GenerateShortlinkDialog = ({ open, onClose }: Props) => {
  const [prompt, setPrompt] = useState('');
  const [copied, setCopied] = useState(false);

  // Reset copied state when prompt changes
  useEffect(() => {
    setCopied(false);
  }, [prompt]);

  const shortlink = useMemo(() => {
    const baseUrl = `${env.NEXT_PUBLIC_APP_URL}/ai`;
    if (!prompt.trim()) {
      return baseUrl;
    }
    const params = new URLSearchParams();
    params.set('prompt', prompt.trim());
    return `${baseUrl}?${params.toString()}`;
  }, [prompt]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(shortlink);
    setCopied(true);
  };

  const handleClose = () => {
    onClose();
    setPrompt('');
    setCopied(false);
  };

  return (
    <Or2Dialog
      open={open}
      onClose={handleClose}
      title="Generate AI Assistant Shortlink"
      ariaLabel="generate-shortlink"
      small
      actions={
        <>
          <Or2Button color="inherit" onClick={handleClose}>
            Close
          </Or2Button>
          <Or2Button color="primary" variant="contained" onClick={handleCopy}>
            {copied ? 'Copied!' : 'Copy'}
          </Or2Button>
        </>
      }
    >
      <Typography sx={{ marginBottom: 2 }}>
        Generate a shortlink to open the AI Assistant. Optionally include a
        pre-filled prompt.
      </Typography>
      <TextField
        label="Prompt (optional)"
        multiline
        rows={3}
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="e.g. How do I add a new governor?"
        sx={{ width: '100%', marginBottom: 2 }}
      />
      <Box>
        <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
          Generated shortlink:
        </Typography>
        <TextField
          value={shortlink}
          slotProps={{
            input: {
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleCopy} edge="end">
                    {copied ? <Check color="success" /> : <ContentCopy />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          sx={{ width: '100%' }}
        />
      </Box>
    </Or2Dialog>
  );
};
